import {
  FROST_BURST_EFFECTS_S,
  type FrostHole,
  flashOpacity,
  frontKey,
  frostFront,
  holesAnimating,
  pointHoles,
  shakeOffset,
  stepHoles,
  stepProgress,
  thawProgressFrom,
} from "./frost";

/**
 * Цикл анимации «ледяного взрыва» (ТЗ §113). Состояние — прогресс `p`, цель и
 * момент нажатия — лежит здесь, а не в React: оно меняется каждый кадр.
 * Кадры запрашиваются, только пока лёд движется или идут вспышка и тряска; в
 * покое цикл остановлен (§113 п. 7).
 *
 * Всё внешнее — часы, `requestAnimationFrame`, отрисовка — приходит параметром:
 * цикл проверяется без браузера.
 */

export interface FrostControllerDeps {
  /** Часы той же шкалы, что и метка времени кадра, мс. */
  now(): number;
  requestFrame(callback: (timeMs: number) => void): number;
  cancelFrame(id: number): void;
  /** Включено ли «уменьшить движение»: тогда лёд появляется и исчезает сразу. */
  reducedMotion(): boolean;
  /** Глубина льда сейчас, px: зависит от размера экрана. */
  coverage(): number;
  /** Размер оверлея, px: круг рождается только над полосой льда у краёв. */
  size(): { width: number; height: number };
  /** Перерисовать лёд при данном положении фронта. */
  draw(front: number): void;
  setFlash(opacity: number): void;
  /** Сдвиг контейнера; `null` — вернуть на место. */
  setShake(offset: { x: number; y: number } | null): void;
  /** Перерисовать круги оттаивания под курсором (§118) при данном радиусе, px. */
  drawHoles(holes: readonly FrostHole[], radius: number): void;
}

export interface FrostController {
  /** Заморозить (`true`) или оттаять (`false`). Повтор текущей цели ничего не делает. */
  set(active: boolean): void;
  /**
   * Курсор мыши в системе оверлея; `null` — курсор ушёл. Учитывается, только пока
   * лежит лёд и задан радиус (§118 п. 3).
   */
  pointer(point: { x: number; y: number } | null): void;
  /** Нужен ли курсор: лёд лежит и радиус задан. Пока `false`, координаты считать незачем. */
  wantsPointer(): boolean;
  /** Радиус круга оттаивания, px; 0 — кругов нет. */
  setThawRadius(radius: number): void;
  /** Перерисовать текущее положение: размер экрана изменился. Кадров не заводит. */
  invalidate(): void;
  /** Снять кадр, погасить вспышку, вернуть контейнер. */
  destroy(): void;
}

/** Скачок времени между кадрами (скрытая вкладка) не должен пролистывать анимацию. */
const MAX_FRAME_S = 0.05;
/** Прогресс, с которого стартует взрыв, если нажали посреди оттаивания. */
const RESTART_PROGRESS = 0.02;

export function createFrostController(deps: FrostControllerDeps): FrostController {
  let progress = 0;
  let target = 0;
  let burstAtMs: number | null = null;
  let previousFrameMs: number | null = null;
  let frameId: number | null = null;
  let lastKey: number | null = null;
  let lastFront: number | null = null;
  let destroyed = false;
  let holes: FrostHole[] = [];
  let thawRadius = 0;

  /** Лёд лежит: заморожено или ещё оттаивает (§118 п. 3). */
  const iceLies = (): boolean => target === 1 || progress > 0;

  const resetHoles = (): void => {
    if (holes.length > 0) {
      holes = [];
      deps.drawHoles(holes, thawRadius);
    }
  };

  /** `force` — перерисовать, даже если фронт сдвинулся меньше чем на 0,25 px: так покой всегда в точном конце. */
  const redraw = (force = false): void => {
    const front = frostFront(progress, target === 1, deps.coverage());
    const key = frontKey(front);
    if ((!force && key === lastKey) || front === lastFront) {
      return;
    }
    lastKey = key;
    lastFront = front;
    deps.draw(front);
  };

  const clearBurst = (): void => {
    burstAtMs = null;
    deps.setFlash(0);
    deps.setShake(null);
  };

  const tick = (timeMs: number): void => {
    frameId = null;
    const dtS =
      previousFrameMs === null ? 0 : Math.min(MAX_FRAME_S, (timeMs - previousFrameMs) / 1000);
    previousFrameMs = timeMs;

    let burstElapsedS: number | null = null;
    if (burstAtMs !== null) {
      // Метка кадра может оказаться чуть раньше момента нажатия.
      burstElapsedS = Math.max(0, (timeMs - burstAtMs) / 1000);
      deps.setFlash(flashOpacity(burstElapsedS));
      deps.setShake(shakeOffset(burstElapsedS));
    }
    progress = stepProgress(progress, target, dtS);
    redraw(progress === target);
    if (!iceLies()) {
      resetHoles();
    } else if (holes.length > 0) {
      holes = stepHoles(holes, dtS);
      deps.drawHoles(holes, thawRadius);
    }

    const bursting = burstElapsedS !== null && burstElapsedS < FROST_BURST_EFFECTS_S;
    if (progress !== target || bursting || holesAnimating(holes)) {
      schedule();
      return;
    }
    previousFrameMs = null;
    clearBurst();
  };

  function schedule(): void {
    if (frameId === null && !destroyed) {
      frameId = deps.requestFrame(tick);
    }
  }

  return {
    set(active) {
      if (destroyed) {
        return;
      }
      const next = active ? 1 : 0;
      if (next === target) {
        return;
      }
      target = next;
      if (deps.reducedMotion()) {
        progress = target;
        clearBurst();
        redraw();
        if (!iceLies()) {
          resetHoles();
        }
        return;
      }
      if (active) {
        burstAtMs = deps.now();
        progress = Math.min(progress, RESTART_PROGRESS);
      } else {
        // Прогресс переводится на кривую оттаивания с тем же фронтом: без скачка.
        progress = thawProgressFrom(progress);
        // Оттаивание идёт без вспышки и тряски, даже если взрыв не закончился.
        clearBurst();
      }
      schedule();
    },
    wantsPointer: () => !destroyed && thawRadius > 0 && iceLies(),
    pointer(rawPoint) {
      if (destroyed || thawRadius <= 0 || !iceLies()) {
        return;
      }
      // Круг рождается только над полосой льда: в чистом центре курсор считается ушедшим.
      let point = rawPoint;
      if (point !== null) {
        const { width, height } = deps.size();
        const edge = Math.min(point.x, point.y, width - point.x, height - point.y);
        if (edge > deps.coverage() + thawRadius) {
          point = null;
        }
      }
      if (point === null && holes.length === 0) {
        return;
      }
      const reduced = deps.reducedMotion();
      let next = pointHoles(holes, point, thawRadius);
      if (reduced) {
        // Без движения: круг сразу полной силы, ушедший пропадает сразу.
        next = next.filter((hole) => !hole.leaving).map((hole) => ({ ...hole, strength: 1 }));
      }
      holes = next;
      deps.drawHoles(holes, thawRadius);
      if (!reduced && holesAnimating(holes)) {
        schedule();
      }
    },
    setThawRadius(radius) {
      const next = Math.max(0, radius);
      if (destroyed || next === thawRadius) {
        return;
      }
      thawRadius = next;
      if (thawRadius <= 0) {
        resetHoles();
      } else if (holes.length > 0) {
        deps.drawHoles(holes, thawRadius);
      }
    },
    invalidate() {
      if (destroyed) {
        return;
      }
      lastKey = null;
      lastFront = null;
      redraw();
    },
    destroy() {
      destroyed = true;
      if (frameId !== null) {
        deps.cancelFrame(frameId);
        frameId = null;
      }
      clearBurst();
    },
  };
}
